import React, { useState, useEffect, useRef } from 'react';
import SessionManager from '../utils/sessionManager';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'dashboard' },
  { id: 'feedback', label: 'Feedback', icon: 'rate_review' },
  { id: 'summary', label: 'Summary', icon: 'summarize' },
  { id: 'transcript', label: 'Transcript', icon: 'subtitles' },
  { id: 'scoring', label: 'Scoring', icon: 'leaderboard' },
];

const HrLiveInterviewReportPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const audioRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    loadReport();
  }, [sessionId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';
      const response = await fetch(`${baseUrl}/live-interview/session/${sessionId}/report`, { headers: buildAuthHeaders() });
      if (!response.ok) throw new Error('Failed to load interview report');
      const data = await response.json();
      setReport(data);
    } catch (err) {
      console.error('Error loading report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Seek both audio and video players
  const seekToTimestamp = (timestamp) => {
    const parts = timestamp.split(':').map(Number);
    let seconds = 0;
    if (parts.length === 3) seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
    else if (parts.length === 2) seconds = parts[0] * 60 + parts[1];

    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play().catch(() => {});
    }
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      if (!videoRef.current) audioRef.current.play().catch(() => {});
    }
  };

  const TimestampBadge = ({ timestamp, className = '' }) => {
    if (!timestamp) return null;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); seekToTimestamp(timestamp); }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer ${className}`}
        title="Click to play from this timestamp"
      >
        <Icon name="play_arrow" size={12} />
        {timestamp}
      </button>
    );
  };

  // Score helpers
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 90) return 'bg-green-100 dark:bg-green-900/20';
    if (score >= 80) return 'bg-blue-100 dark:bg-blue-900/20';
    if (score >= 70) return 'bg-yellow-100 dark:bg-yellow-900/20';
    if (score >= 60) return 'bg-orange-100 dark:bg-orange-900/20';
    return 'bg-red-100 dark:bg-red-900/20';
  };

  const getScoreBarColor = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 80) return 'bg-blue-500';
    if (score >= 70) return 'bg-yellow-500';
    if (score >= 60) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getHireColor = (decision) => {
    const d = (decision || '').toLowerCase();
    if (d.includes('strongly recommend')) return { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300', border: 'border-green-300 dark:border-green-700', ring: 'ring-green-500' };
    if (d.includes('recommend') && !d.includes('not')) return { bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-300 dark:border-blue-700', ring: 'ring-blue-500' };
    if (d.includes('consider')) return { bg: 'bg-yellow-50 dark:bg-yellow-900/20', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-700', ring: 'ring-yellow-500' };
    return { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-700', ring: 'ring-red-500' };
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'positive') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    if (sentiment === 'negative') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading interview report...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !report) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <Icon name="error" size={64} className="text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Failed to Load Report</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error || 'Interview report not found'}</p>
            <Button onClick={() => navigate('/hr/dashboard')} variant="primary">Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  const evaluation = report.evaluation || {};
  const transcript = report.transcript || [];
  const hasAudioEvaluation = evaluation?.full_transcript?.length > 0;
  const hireRec = evaluation?.hire_recommendation;
  const commAssessment = evaluation?.communication_assessment;

  // ──────────── TAB CONTENT RENDERERS ────────────

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Candidate Info + Score Card */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Candidate Details */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Candidate Information</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: 'person', label: 'Candidate', value: report.candidate_name },
              { icon: 'email', label: 'Email', value: report.candidate_email },
              { icon: 'work', label: 'Position', value: report.job_title },
              { icon: 'business', label: 'Company', value: report.company },
              { icon: 'timer', label: 'Duration', value: `${report.duration_minutes} minutes` },
              { icon: 'calendar_today', label: 'Completed', value: report.completed_at ? new Date(report.completed_at).toLocaleString() : 'N/A' },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <Icon name={item.icon} size={18} className="text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{item.value || 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Score Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center text-center">
          <div className="relative w-32 h-32 mb-4">
            <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="54" strokeWidth="8" fill="none" className="stroke-gray-200 dark:stroke-gray-700" />
              <circle
                cx="60" cy="60" r="54" strokeWidth="8" fill="none"
                strokeLinecap="round"
                className={`${getScoreBarColor(report.overall_score || 0).replace('bg-', 'stroke-')}`}
                style={{
                  strokeDasharray: `${2 * Math.PI * 54}`,
                  strokeDashoffset: `${2 * Math.PI * 54 * (1 - (report.overall_score || 0) / 100)}`,
                  transition: 'stroke-dashoffset 1s ease-out',
                }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-3xl font-bold ${getScoreColor(report.overall_score || 0)}`}>
                {Math.round(report.overall_score || 0)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">out of 100</span>
            </div>
          </div>
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Overall Score
          </div>
          {report.overall_grade && (
            <span className={`mt-2 px-3 py-1 rounded-full text-xs font-bold ${getScoreBgColor(report.overall_score || 0)} ${getScoreColor(report.overall_score || 0)}`}>
              Grade: {report.overall_grade}
            </span>
          )}
        </div>
      </div>

      {/* Hire Recommendation */}
      {hireRec && (
        <div className={`rounded-2xl p-5 border-2 ${getHireColor(hireRec.decision).bg} ${getHireColor(hireRec.decision).border}`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getHireColor(hireRec.decision).bg} ring-2 ${getHireColor(hireRec.decision).ring}`}>
                <Icon
                  name={hireRec.decision?.toLowerCase().includes('not') ? 'cancel' : 'verified'}
                  size={28}
                  className={getHireColor(hireRec.decision).text}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Hire Recommendation</p>
                <p className={`text-xl font-bold ${getHireColor(hireRec.decision).text}`}>{hireRec.decision}</p>
              </div>
            </div>
            {hireRec.confidence != null && (
              <div className="text-center px-4">
                <span className={`text-2xl font-bold ${getScoreColor(hireRec.confidence)}`}>{hireRec.confidence}%</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">Confidence</p>
              </div>
            )}
          </div>
          {hireRec.reasoning && (
            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{hireRec.reasoning}</p>
          )}
        </div>
      )}

      {/* Media Players */}
      <div className="grid lg:grid-cols-2 gap-6">
        {report.recording_url && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Icon name="videocam" size={20} className="text-blue-500" />
              Interview Recording
            </h3>
            <video
              ref={videoRef}
              controls
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
              src={report.recording_url}
              preload="metadata"
            />
          </div>
        )}
        {report.audio_url && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Icon name="headphones" size={20} className="text-indigo-500" />
              Interview Audio
            </h3>
            <audio ref={audioRef} controls className="w-full" src={report.audio_url} preload="metadata" />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Click any timestamp in the report to jump to that point.
            </p>
          </div>
        )}
      </div>

      {/* Session Summary */}
      {(evaluation.session_summary || evaluation.comprehensive_feedback) && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Icon name="description" size={20} className="text-teal-500" />
            Session Summary
          </h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
            {evaluation.session_summary || evaluation.comprehensive_feedback}
          </p>
        </div>
      )}

      {/* Quick Stats */}
      {commAssessment && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Clarity', score: commAssessment.clarity_score, icon: 'visibility' },
            { label: 'Confidence', score: commAssessment.confidence_score, icon: 'psychology' },
            { label: 'Fluency', score: commAssessment.fluency_score, icon: 'record_voice_over' },
            { label: 'Professionalism', score: commAssessment.professionalism_score, icon: 'business_center' },
          ].filter(i => i.score != null).map((item) => (
            <div key={item.label} className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
              <Icon name={item.icon} size={24} className="text-gray-400 mx-auto mb-2" />
              <div className={`text-2xl font-bold ${getScoreColor(item.score)}`}>{item.score}%</div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-1">{item.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderFeedbackTab = () => (
    <div className="space-y-6">
      {/* Strengths & Weaknesses Side by Side */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Strengths */}
        {(evaluation.strengths_with_timestamps?.length > 0 || evaluation.strengths?.length > 0) && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Icon name="thumb_up" size={18} className="text-green-600" />
              </div>
              Strengths
            </h3>
            <div className="space-y-3">
              {evaluation.strengths_with_timestamps?.length > 0
                ? evaluation.strengths_with_timestamps.map((item, i) => (
                  <div key={i} className="p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-100 dark:border-green-800/30">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.strength}</p>
                    {item.context && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.context}</p>}
                    {item.timestamp && <TimestampBadge timestamp={item.timestamp} className="mt-2" />}
                  </div>
                ))
                : evaluation.strengths?.map((s, i) => (
                  <div key={i} className="p-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-100 dark:border-green-800/30">
                    <p className="text-sm text-gray-700 dark:text-gray-300">{s}</p>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* Weaknesses */}
        {(evaluation.weaknesses_with_timestamps?.length > 0 || evaluation.areas_for_improvement?.length > 0) && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Icon name="lightbulb" size={18} className="text-amber-600" />
              </div>
              Areas for Improvement
            </h3>
            <div className="space-y-3">
              {evaluation.weaknesses_with_timestamps?.length > 0
                ? evaluation.weaknesses_with_timestamps.map((item, i) => (
                  <div key={i} className="p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-100 dark:border-amber-800/30">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{item.weakness}</p>
                    {item.context && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.context}</p>}
                    {item.timestamp && <TimestampBadge timestamp={item.timestamp} className="mt-2" />}
                  </div>
                ))
                : evaluation.areas_for_improvement?.map((area, i) => (
                  <div key={i} className="p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-100 dark:border-amber-800/30">
                    <p className="text-sm text-gray-700 dark:text-gray-300">{area}</p>
                  </div>
                ))
              }
            </div>
          </div>
        )}
      </div>

      {/* Key Highlights */}
      {evaluation.highlights?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Icon name="auto_awesome" size={20} className="text-amber-500" />
            Key Highlights
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {evaluation.highlights.map((h, i) => (
              <div key={i} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getSentimentColor(h.sentiment)}`}>
                    {h.sentiment}
                  </span>
                  {h.timestamp && <TimestampBadge timestamp={h.timestamp} />}
                </div>
                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">{h.title}</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">{h.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations (old format fallback) */}
      {evaluation.recommendations && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Recommendations</h3>
          <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">{evaluation.recommendations}</p>
        </div>
      )}
    </div>
  );

  const renderSummaryTab = () => (
    <div className="space-y-6">
      {/* Communication Assessment */}
      {commAssessment && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
            <Icon name="record_voice_over" size={20} className="text-purple-500" />
            Communication Assessment
          </h3>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Score Bars */}
            <div className="space-y-5">
              {[
                { label: 'Clarity', score: commAssessment.clarity_score, icon: 'visibility' },
                { label: 'Confidence', score: commAssessment.confidence_score, icon: 'psychology' },
                { label: 'Fluency', score: commAssessment.fluency_score, icon: 'record_voice_over' },
                { label: 'Professionalism', score: commAssessment.professionalism_score, icon: 'business_center' },
              ].filter(i => i.score != null).map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <Icon name={item.icon} size={16} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.label}</span>
                    </div>
                    <span className={`text-sm font-bold ${getScoreColor(item.score)}`}>{item.score}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-700 ${getScoreBarColor(item.score)}`}
                      style={{ width: `${item.score}%` }}
                    />
                  </div>
                </div>
              ))}
              {commAssessment.overall_communication_score != null && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-900 dark:text-white">Overall Communication</span>
                    <span className={`text-xl font-bold ${getScoreColor(commAssessment.overall_communication_score)}`}>
                      {commAssessment.overall_communication_score}%
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {commAssessment.speaking_pace && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Speaking Pace</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{commAssessment.speaking_pace}</p>
                  </div>
                )}
                {commAssessment.pronunciation && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Pronunciation</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{commAssessment.pronunciation}</p>
                  </div>
                )}
                {commAssessment.filler_words_count != null && (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg col-span-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Filler Words</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {commAssessment.filler_words_count} detected
                      {commAssessment.filler_words?.length > 0 && (
                        <span className="font-normal text-gray-500 ml-1">({commAssessment.filler_words.join(', ')})</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
              {commAssessment.feedback && (
                <div className="p-4 bg-purple-50 dark:bg-purple-900/10 rounded-lg border border-purple-100 dark:border-purple-800/30">
                  <p className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-1">Feedback</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{commAssessment.feedback}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Skills Assessment */}
      {evaluation.skills_assessment?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-5 flex items-center gap-2">
            <Icon name="insights" size={20} className="text-blue-500" />
            Skills Assessment
          </h3>
          <div className="space-y-5">
            {evaluation.skills_assessment.map((skill, i) => (
              <div key={i}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{skill.skill}</span>
                  <span className={`font-bold ${getScoreColor(skill.score)}`}>{skill.score}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-700 ${getScoreBarColor(skill.score)}`}
                    style={{ width: `${skill.score}%` }}
                  />
                </div>
                {skill.feedback && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-relaxed">{skill.feedback}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Session Summary (if not shown in overview) */}
      {(evaluation.session_summary || evaluation.comprehensive_feedback) && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Icon name="description" size={20} className="text-teal-500" />
            Overall Assessment
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
            {evaluation.session_summary || evaluation.comprehensive_feedback}
          </p>
        </div>
      )}
    </div>
  );

  const renderTranscriptTab = () => {
    const transcriptData = hasAudioEvaluation ? evaluation.full_transcript : transcript;

    if (!transcriptData?.length) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <Icon name="subtitles_off" size={48} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No transcript available for this interview.</p>
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Icon name="subtitles" size={18} className="text-teal-500" />
              Full Transcript ({transcriptData.length} messages)
            </h3>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-purple-200 dark:bg-purple-800 inline-block"></span> Interviewer</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-200 dark:bg-blue-800 inline-block"></span> Candidate</span>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          {transcriptData.map((turn, index) => {
            const isCandidate = turn.speaker === 'candidate' || turn.role === 'user';
            const text = turn.text || turn.content || '';

            return (
              <div
                key={index}
                className={`flex ${isCandidate ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${isCandidate ? 'order-1' : ''}`}>
                  <div className={`flex items-center gap-2 mb-1 ${isCandidate ? 'justify-end' : ''}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isCandidate ? 'bg-blue-100 dark:bg-blue-900/40 order-2' : 'bg-purple-100 dark:bg-purple-900/40'
                    }`}>
                      <Icon
                        name={isCandidate ? 'person' : 'smart_toy'}
                        size={14}
                        className={isCandidate ? 'text-blue-600' : 'text-purple-600'}
                      />
                    </div>
                    <span className={`text-xs font-medium text-gray-500 dark:text-gray-400 ${isCandidate ? 'order-1' : ''}`}>
                      {isCandidate ? 'Candidate' : 'Interviewer'}
                    </span>
                    {turn.start_time && <TimestampBadge timestamp={turn.start_time} />}
                  </div>
                  <div className={`p-3 rounded-2xl text-sm leading-relaxed ${
                    isCandidate
                      ? 'bg-blue-500 text-white rounded-tr-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-tl-sm'
                  }`}>
                    {text}
                  </div>
                  {turn.end_time && (
                    <p className={`text-[10px] text-gray-400 mt-0.5 ${isCandidate ? 'text-right' : ''}`}>
                      ends {turn.end_time}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderScoringTab = () => (
    <div className="space-y-6">
      {/* Per-Question Breakdown */}
      {evaluation.questions_and_answers?.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Icon name="quiz" size={20} className="text-blue-500" />
              Question-by-Question Breakdown
            </h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {evaluation.questions_and_answers.length} questions
            </span>
          </div>

          {evaluation.questions_and_answers.map((qa, index) => {
            const isExpanded = expandedQuestions[index];
            return (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button
                  onClick={() => setExpandedQuestions(prev => ({ ...prev, [index]: !prev[index] }))}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex items-center justify-center text-sm font-bold">
                      Q{qa.question_number || index + 1}
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{qa.question}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                    {qa.start_time && <TimestampBadge timestamp={qa.start_time} />}
                    {qa.score != null && (
                      <div className={`px-3 py-1 rounded-lg text-sm font-bold ${getScoreBgColor(qa.score)} ${getScoreColor(qa.score)}`}>
                        {Math.round(qa.score)}%
                      </div>
                    )}
                    <Icon name={isExpanded ? 'expand_less' : 'expand_more'} size={24} className="text-gray-400" />
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                    {/* Score bar */}
                    {qa.score != null && (
                      <div className="mb-4">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div className={`h-2 rounded-full transition-all duration-500 ${getScoreBarColor(qa.score)}`} style={{ width: `${qa.score}%` }} />
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      {/* Question */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Question</p>
                        <p className="text-sm text-gray-900 dark:text-white">{qa.question}</p>
                        <div className="flex gap-2 mt-1.5">
                          {qa.start_time && <TimestampBadge timestamp={qa.start_time} />}
                          {qa.end_time && <TimestampBadge timestamp={qa.end_time} />}
                        </div>
                      </div>

                      {/* Answer */}
                      <div>
                        <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Candidate's Answer</p>
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800/30">
                          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{qa.answer}</p>
                        </div>
                      </div>

                      {/* Evaluation */}
                      {qa.explanation && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Evaluation</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{qa.explanation}</p>
                        </div>
                      )}

                      {/* Better Approach */}
                      {qa.better_approach && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-800/30">
                          <p className="text-xs font-bold text-blue-700 dark:text-blue-300 mb-1">Suggested Better Approach</p>
                          <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">{qa.better_approach}</p>
                        </div>
                      )}

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        {qa.strengths_shown?.map((s, i) => (
                          <span key={`s-${i}`} className="px-2.5 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-full text-xs font-medium">
                            {s}
                          </span>
                        ))}
                        {qa.key_missing_elements?.map((m, i) => (
                          <span key={`m-${i}`} className="px-2.5 py-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full text-xs font-medium">
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
          <Icon name="quiz" size={48} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No per-question scoring available for this interview.</p>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverviewTab();
      case 'feedback': return renderFeedbackTab();
      case 'summary': return renderSummaryTab();
      case 'transcript': return renderTranscriptTab();
      case 'scoring': return renderScoringTab();
      default: return renderOverviewTab();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/hr/dashboard')} variant="outline" size="sm">
              <Icon name="arrow_back" size={18} className="mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Interview Report
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {report.candidate_name} — {report.job_title}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Score pill */}
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${getScoreBgColor(report.overall_score || 0)}`}>
              <span className={`text-lg font-bold ${getScoreColor(report.overall_score || 0)}`}>
                {Math.round(report.overall_score || 0)}%
              </span>
              {report.overall_grade && (
                <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                  ({report.overall_grade})
                </span>
              )}
            </div>
            <Button onClick={() => window.print()} variant="outline" size="sm">
              <Icon name="print" size={18} />
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6 p-1 flex gap-1 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Icon name={tab.icon} size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {renderTabContent()}

        {/* Bottom Actions */}
        <div className="mt-8 mb-8 flex gap-4 justify-center print:hidden">
          <Button onClick={() => window.print()} variant="outline">
            <Icon name="print" size={20} className="mr-2" />
            Print Report
          </Button>
          <Button onClick={() => navigate('/hr/dashboard')} variant="primary">
            Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HrLiveInterviewReportPage;
